import { useState } from 'react'
import {
  useClientProfilesWithCounts,
  useClientProfileMutations,
} from '../../hooks/use-client-profiles'
import type {
  Case,
  ClientProfileWithCaseCount,
  ClientProfileStatus,
  CreateClientProfileInput,
} from '../../types/case-management'
import { EMPTY_FORM_DATA, type DeleteOption } from './types'
import { useClientsActions, type ClientsActions } from './use-clients-actions'

export interface ModalState {
  showCreateModal: boolean
  setShowCreateModal: (value: boolean) => void
  showEditModal: boolean
  setShowEditModal: (value: boolean) => void
  showDeleteModal: boolean
  setShowDeleteModal: (value: boolean) => void
  showLinkCaseModal: boolean
  setShowLinkCaseModal: (value: boolean) => void
  selectedClient: ClientProfileWithCaseCount | null
  setSelectedClient: (value: ClientProfileWithCaseCount | null) => void
  actionMenuClient: string | null
  setActionMenuClient: (value: string | null) => void
}

function useModalState(): ModalState {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showLinkCaseModal, setShowLinkCaseModal] = useState(false)
  const [selectedClient, setSelectedClient] = useState<ClientProfileWithCaseCount | null>(null)
  const [actionMenuClient, setActionMenuClient] = useState<string | null>(null)
  return {
    showCreateModal,
    setShowCreateModal,
    showEditModal,
    setShowEditModal,
    showDeleteModal,
    setShowDeleteModal,
    showLinkCaseModal,
    setShowLinkCaseModal,
    selectedClient,
    setSelectedClient,
    actionMenuClient,
    setActionMenuClient,
  }
}

export interface FormState {
  formData: CreateClientProfileInput
  setFormData: (data: CreateClientProfileInput) => void
  formLoading: boolean
  setFormLoading: (loading: boolean) => void
  formError: string | null
  setFormError: (error: string | null) => void
}

function useFormState(): FormState {
  const [formData, setFormData] = useState<CreateClientProfileInput>(EMPTY_FORM_DATA)
  const [formLoading, setFormLoading] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  return { formData, setFormData, formLoading, setFormLoading, formError, setFormError }
}

export interface DeleteState {
  deleteOption: DeleteOption
  setDeleteOption: (option: DeleteOption) => void
  reassignTargetId: string
  setReassignTargetId: (id: string) => void
}

function useDeleteState(): DeleteState {
  const [deleteOption, setDeleteOption] = useState<DeleteOption>('unlink')
  const [reassignTargetId, setReassignTargetId] = useState<string>('')
  return { deleteOption, setDeleteOption, reassignTargetId, setReassignTargetId }
}

export interface LinkState {
  unlinkedCases: Case[]
  setUnlinkedCases: (cases: Case[]) => void
  unlinkedCasesLoading: boolean
  setUnlinkedCasesLoading: (loading: boolean) => void
  selectedCaseToLink: string
  setSelectedCaseToLink: (id: string) => void
}

function useLinkState(): LinkState {
  const [unlinkedCases, setUnlinkedCases] = useState<Case[]>([])
  const [unlinkedCasesLoading, setUnlinkedCasesLoading] = useState(false)
  const [selectedCaseToLink, setSelectedCaseToLink] = useState<string>('')
  return {
    unlinkedCases,
    setUnlinkedCases,
    unlinkedCasesLoading,
    setUnlinkedCasesLoading,
    selectedCaseToLink,
    setSelectedCaseToLink,
  }
}

interface FilterState {
  searchQuery: string
  setSearchQuery: (value: string) => void
  statusFilter: ClientProfileStatus | ''
  setStatusFilter: (value: ClientProfileStatus | '') => void
  params: { search?: string; status?: ClientProfileStatus }
}

function useFilterState(): FilterState {
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<ClientProfileStatus | ''>('')
  const params: { search?: string; status?: ClientProfileStatus } = {}
  if (searchQuery) params.search = searchQuery
  if (statusFilter) params.status = statusFilter
  return { searchQuery, setSearchQuery, statusFilter, setStatusFilter, params }
}

export interface ClientsStateInput {
  isAdmin: boolean
}

export interface ClientsState {
  profiles: ClientProfileWithCaseCount[]
  loading: boolean
  error: string | null
  searchQuery: string
  setSearchQuery: (value: string) => void
  statusFilter: ClientProfileStatus | ''
  setStatusFilter: (value: ClientProfileStatus | '') => void
  modal: ModalState
  form: FormState
  del: DeleteState
  link: LinkState
  actions: ClientsActions
}

export function useClientsState(input: ClientsStateInput): ClientsState {
  const { isAdmin } = input
  const filter = useFilterState()
  const profilesQuery = useClientProfilesWithCounts(filter.params)
  const mutations = useClientProfileMutations()
  const modal = useModalState()
  const form = useFormState()
  const del = useDeleteState()
  const link = useLinkState()
  const actions = useClientsActions({
    isAdmin,
    modal,
    form,
    del,
    link,
    mutations: {
      create: mutations.create,
      update: mutations.update,
      remove: mutations.remove,
      mutationError: mutations.error,
      refetch: profilesQuery.refetch,
    },
  })
  return {
    profiles: profilesQuery.profiles,
    loading: profilesQuery.loading,
    error: profilesQuery.error,
    searchQuery: filter.searchQuery,
    setSearchQuery: filter.setSearchQuery,
    statusFilter: filter.statusFilter,
    setStatusFilter: filter.setStatusFilter,
    modal,
    form,
    del,
    link,
    actions,
  }
}
